import type { Response, WidgetT } from 'lib-shared-legacy-types';
import type { ApiResponse, Queue } from 'lib-shared-types-api';
import noop from 'lodash/noop';
import type { Action } from 'redux-actions';
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

import { ajaxSend } from 'api/api';
import type { NodeDataT } from 'fable/flowEditor';
import {
  ApiActionT,
  sagaApiGenWithPromise,
} from 'utils/sagaApiGeneratorHelpers';

import { FlowNodeTypes } from '../../powerFlowShared/components/types';
import { getRequestID } from '../../shared/util/request';
import { addFlow, getFlow, updateFlow } from '../api/chatFlow';
import type {
  FlowChatActionT,
  FlowChatAddFlowPayloadT,
  FlowChatCreateFlowT,
} from '../api/types';
import {
  chatFlowAdd,
  chatFlowAddAfterChatWidgetCreated,
  chatFlowAddFailed,
  chatFlowAddSucceeded,
  chatFlowEdit,
  chatFlowEditFailed,
  chatFlowEditSucceeded,
  chatFlowGet,
  chatFlowGetFailed,
  chatFlowGetSucceeded,
} from '../reducers/chatFlow';

type WidgetActionT = {
  result: Response<
    WidgetT & {
      widget: Pick<
        WidgetT,
        | 'created_at'
        | 'id'
        | 'name'
        | 'organization_id'
        | 'queue_id'
        | 'updated_at'
      >;
    }
  >;
};

function getQueue(queueId: string): Promise<ApiResponse<Queue>> {
  return ajaxSend({
    path: `/v2/queues/${queueId}`,
  });
}

function buildDefault(
  apiAction: ApiActionT<FlowChatAddFlowPayloadT, NodeDataT<FlowChatActionT[]>>,
) {
  return function* (action: Action<WidgetActionT>) {
    let selectedQueue: null | Queue = null;
    const initNodeId = getRequestID();
    const Node2ndId = getRequestID();
    const Node3rdId = getRequestID();
    // Get settings (we need the default queue id).
    const getSettings = (state) => state.settings;
    const settingsState = yield select(getSettings);
    console.info(
      settingsState,
      settingsState.organization.organization.default_queue,
    );

    // Get default queue by id.
    if (
      settingsState.organization.organization.default_queue !== undefined &&
      settingsState.organization.organization.default_queue !== null &&
      action.payload.result.data.widget.queue_id
    ) {
      const queueResult = yield call(
        getQueue,
        action.payload.result.data.widget.queue_id,
      );

      if (queueResult && queueResult.data) {
        selectedQueue = queueResult.data;
      }
    }

    // Build the default flow with the default queue and number.
    if (selectedQueue !== null) {
      const defaultFlow: FlowChatCreateFlowT = {
        actions: [
          {
            badges: [],
            childIds: [],
            connectionLabel: 'arrow',
            icon: null,
            id: initNodeId,
            isAction: false,
            isEditable: false,
            isRemovable: false,
            nodeType: FlowNodeTypes.INIT,
            overlay: [],
            pageX: 1715,
            pageY: 810,
            parentId: null,
            payload: null,
            text: 'Incoming chat',
          },
          {
            badges: [],
            childIds: [],
            connectionLabel: 'arrow',
            icon: 'i-widget-customize',
            id: Node2ndId,
            isAction: false,
            isEditable: true,
            isRemovable: true,
            nodeType: FlowNodeTypes.WIDGET_CUSTOMIZATION,
            overlay: [],
            pageX: 1626,
            pageY: 906,
            parentId: initNodeId,
            payload: {
              localizations: {
                'en-US': {
                  minimized: {
                    html_text_online: 'Need help? **We are online**',
                    html_text_offline: 'Get in touch',
                  },
                },
              },
              medium: {
                chatbox_view: {
                  position: 'bottom_right',
                },
                minimized_view: {
                  position: 'bottom_right',
                },
              },
              small: {
                chatbox_view: {
                  position: 'bottom_right',
                },
                minimized_view: {
                  position: 'bottom_right',
                },
              },
              theme: {
                color: '#6474ba',
                color_darken: '#3e4c8b',
                color_lighten: '#9fa9d4',
                text_color: '#ffffff',
              },
              nextNodeId: Node3rdId,
            },
            text: 'Widget Customization',
          },
          {
            badges: [],
            childIds: [],
            connectionLabel: 'arrow',
            icon: 'i-queue',
            id: Node3rdId,
            isAction: false,
            isEditable: true,
            isRemovable: true,
            nodeType: FlowNodeTypes.QUEUE,
            overlay: [],
            pageX: 1715,
            pageY: 1094,
            parentId: Node2ndId,
            payload: {
              queue: {
                value: selectedQueue.id,
                label: selectedQueue.name,
              },
            },
            text: `Queue (${selectedQueue.name})`,
          },
        ],
        active: true,
        channel: 'widgetchat',
        location: action.payload.result.data.id,
        name: action.payload.result.data.name,
        number: action.payload.result.data.id,
      };

      yield call(apiAction, { flow: defaultFlow });
      yield put({
        type: 'CHAT_FLOWS_GET_ALL',
        meta: { reject: noop, resolve: noop },
      }); // refetch the widgets.
    }
  };
}

// Saga
export const chatFlow = [
  takeLatest(
    chatFlowGet.toString(),
    sagaApiGenWithPromise(getFlow, chatFlowGetSucceeded, chatFlowGetFailed),
  ),
  takeEvery(
    chatFlowEdit.toString(),
    sagaApiGenWithPromise(
      updateFlow,
      chatFlowEditSucceeded,
      chatFlowEditFailed,
    ),
  ),
  takeEvery(
    chatFlowAdd.toString(),
    sagaApiGenWithPromise(addFlow, chatFlowAddSucceeded, chatFlowAddFailed),
  ),
  takeEvery(
    chatFlowAddAfterChatWidgetCreated.toString(),
    buildDefault(addFlow),
  ),
];
